import {debounce} from "../../../utils";

import {
    Clock,
    FileLoader,
    LoadingManager,
    Mesh,
    OrthographicCamera,
    PlaneGeometry,
    Scene,
    ShaderMaterial,
    SRGBColorSpace,
    TextureLoader,
    WebGLRenderer
} from "three";

class HeroCollegePattern {
    constructor(el, color) {
        this.el = el;
        this.isMobile = this.getIsMobile();
        this.currentColor = color;
        this.clock = new Clock();
        this.binds();
        this.setSizes();
        this.initLoader();
    }

    getIsMobile() {
        return window.matchMedia('(max-width: 640px)').matches;
    }

    setSizes() {
        this.sizes = {
            width: this.el.offsetWidth,
            height: this.el.offsetHeight,
            dpr: Math.min(window.devicePixelRatio, 2),
            aspectRatio: this.el.clientWidth / this.el.clientHeight
        }
    }

    initLoader (){
        const manager = new LoadingManager();
        this.loader = new TextureLoader(manager);
        this.fileLoader = new FileLoader(manager);

        this.images = {}

        this.loader.load(`${window.assets_url}/images/patterns/hero-topo-pattern-college.svg`, (texture) => {
            texture.colorSpace = SRGBColorSpace;
            this.images.desktop = {
                map: texture,
                width: texture.image.width,
                height: texture.image.height
            }
        });

        this.loader.load(`${window.assets_url}/images/patterns/hero-topo-pattern-college-mobile.svg`, (texture) => {
            texture.colorSpace = SRGBColorSpace;
            this.images.mobile = {
                map: texture,
                width: texture.image.width,
                height: texture.image.height
            }
        });

        this.shader = {};

        this.fileLoader.load(`${window.assets_url}/shaders/talent/fragment.glsl`, (data) => {
            this.shader.fragment = data;
        });

        this.fileLoader.load(`${window.assets_url}/shaders/talent/vertex.glsl`, (data) => {
            this.shader.vertex = data;
        });


        manager.onLoad = () => {
            this.onLoad();
        }
    }

    onLoad () {
        this.initialize();
    }

    initialize () {
        this.createScene();
        this.createCamera();
        this.createRenderer();
        this.addObjects();
        this.addListeners();
        this.render();
    }

    createScene () {
        this.scene = new Scene();
    }

    createCamera () {
        // orthographic camera for 2D el size
        this.camera = new OrthographicCamera(
            this.sizes.width / -2,
            this.sizes.width / 2,
            this.sizes.height / 2,
            this.sizes.height / -2,
            1,
            1000
        );

        this.camera.position.z = 1;
    }

    createRenderer () {
        this.renderer = new WebGLRenderer({
            antialias: true,
            alpha: true
        });
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(this.sizes.dpr);
        this.el.appendChild(this.renderer.domElement);
    }

    createShaderMaterial() {
        const texture = this.isMobile ? this.images.mobile : this.images.desktop;
        const uniforms = {
            uColor: { type: 'v3', value: [this.currentColor.r, this.currentColor.g, this.currentColor.b] },
            uAlphaMaskTexture: { type: 't', value: texture.map},
            uResolution: { type: 'v4', value: this.resolution },
            uTime: { type: 'f', value: 0 }
        }

        return new ShaderMaterial({
            vertexShader: this.shader.vertex,
            fragmentShader: this.shader.fragment,
            uniforms,
        });
    }

    addObjects() {
        const sceneWidth = this.sizes.width;
        const sceneHeight = this.sizes.height;
        this.resolution = this.calcResolution();

        const planeGeometry = new PlaneGeometry(sceneWidth, sceneHeight, 1, 1);
        this.material = this.createShaderMaterial();
        this.plane = new Mesh(planeGeometry, this.material);
        this.scene.add(this.plane);
    }

    render() {
        requestAnimationFrame(() => this.render());

        const elapsedTime = this.clock.getElapsedTime();

        this.plane.material.uniforms.uTime.value = elapsedTime * 0.1;

        this.renderer.render(this.scene, this.camera);
    }

    calcResolution () {
        const { width, height } = this.sizes;
        const texture = this.isMobile ? this.images.mobile : this.images.desktop;
        const imageAspect = texture.height / texture.width;
        const screenAspect = height / width;
        let a1;
        let a2;
        if (screenAspect > imageAspect) {
            a1 = (width / height) * imageAspect;
            a2 = 1;
        } else {
            a1 = 1;
            a2 = screenAspect / imageAspect;
        }
        return { x: width, y: height, z: a1, w: a2 };
    }

    updateColor(color) {
        this.plane.material.uniforms.uColor.value = [color.r, color.g, color.b];
    }

    binds() {
        this.onResize = this.onResize.bind(this);
    }

    addListeners () {
        window.addEventListener('resize', debounce(this.onResize, 100));
    }

    onResize () {
        if(this.sizes.width !== this.el.clientWidth || this.sizes.height !== this.el.clientHeight) {
            this.resize();
        }
    }

    resize () {
        this.setSizes();
        this.camera.aspect = this.sizes.aspectRatio;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.resizeObjects()
    }

    resizeObjects () {
        this.material.uniforms.uAlphaMaskTexture.value = this.isMobile ? this.images.mobile.map : this.images.desktop.map;
        this.material.uniforms.uResolution.value = this.calcResolution();
    }
}

export default HeroCollegePattern
